import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import {
  Button,
  CHAT_COLUMN_CLASS,
  PageBody,
  Spinner,
  Tab,
  TabList,
  backLinkVariants,
  cn,
  useNow,
} from '@wundo/ui';
import type { InsightChatReport } from '@wundo/shared/insightChat.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { runDuration } from '@/components/insights/runStatus.ts';
import { RunChatPanel } from '@/components/insights/RunChatPanel.tsx';
import { RunComposer } from '@/components/insights/RunComposer.tsx';
import { RunFacts } from '@/components/insights/RunFacts.tsx';
import { RunReportTab } from '@/components/insights/RunReportTab.tsx';
import { ReportDialog } from '@/components/insights/ReportDialog.tsx';
import { useRunChat } from '@/components/insights/useRunChat.ts';
import { RUN_TABS, useRunTabs } from '@/components/insights/useRunTabs.ts';
import { askRun, fetchRun, stopRun } from '@/lib/insights.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';

export const Route = createFileRoute('/_app/insights/runs/$id')({
  staticData: { orgScope: 'none' },
  component: RunRoute,
});

function RunRoute() {
  const { id } = Route.useParams();

  return <InsightRunPage runId={id} />;
}

/**
 * While the analyst works the socket says when the run changed; this is the
 * fallback for a socket that is down, in milliseconds.
 */
const POLL_MS = 5000;

export interface InsightRunPageProps {
  runId: string;
}

/**
 * One run of a routine on two tabs: its report in the latest version, and the
 * chat with its analyst, from what it did during the run to the questions staff
 * asked after. A run still going, or one without a report, opens on the chat.
 */
export function InsightRunPage({ runId }: InsightRunPageProps) {
  const queryClient = useQueryClient();
  const [openReport, setOpenReport] = useState<InsightChatReport | null>(null);

  const run = useQuery({
    queryKey: ['insightRun', runId],
    queryFn: () => fetchRun(runId),
    refetchInterval: (query) =>
      query.state.data?.chat_state === 'working' ? POLL_MS : false,
    /*
     * Including while this tab is not the focused one, which is the whole point
     * of leaving a run open: react-query would otherwise pause the interval and
     * the page would sit there claiming to refresh.
     */
    refetchIntervalInBackground: true,
  });

  const detail = run.data;
  const working = detail?.chat_state === 'working';
  const chat = useRunChat(runId, detail?.conversation_id, working);
  /* While a run is going the clock keeps running: measure it against now. */
  const now = useNow(working ? POLL_MS : undefined);

  const { active, select } = useRunTabs(detail, working);

  const stop = useMutation({
    mutationFn: () => stopRun(runId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['insightRun', runId] }),
  });

  const ask = useMutation({
    mutationFn: (text: string) => askRun(runId, text),
    onSuccess: (question) => {
      chat.addItem(question);
      void queryClient.invalidateQueries({ queryKey: ['insightRun', runId] });
    },
  });

  const duration = detail
    ? runDuration(detail.started_at, detail.finished_at ?? now.toISOString())
    : null;
  const askError = ask.isError
    ? apiErrorMessage(ask.error, 'The question could not be sent')
    : null;

  return (
    /*
     * One readable column, the width the customer chat reads at, as tall as the
     * window: the header and tabs stay put and each tab scrolls inside itself.
     */
    <div className={cn(CHAT_COLUMN_CLASS, 'flex min-h-0 flex-1 flex-col')}>
      <Link to='/insights' className={backLinkVariants({ className: 'mb-2.5' })}>
        <ChevronLeft aria-hidden='true' />
        Data insights
      </Link>

      <PageHeader
        title={detail?.routine_name ?? 'Insight run'}
        filters={
          <div className='flex items-center gap-2'>
            {working && (
              <Button
                variant='danger'
                size='sm'
                loading={stop.isPending}
                disabled={stop.isPending}
                onClick={() => stop.mutate()}
              >
                Stop
              </Button>
            )}
          </div>
        }
      />

      <PageBody fill className='mt-3.5 flex flex-col'>
        {run.isPending && <Spinner />}

        {run.isError && (
          <p role='alert' className='text-base text-ink-muted'>
            Failed to load the run
          </p>
        )}

        {detail && (
          <div className='flex min-h-0 flex-1 flex-col gap-5.5'>
            <RunFacts run={detail} duration={duration} />

            {detail.error && (
              <p
                role='alert'
                className='rounded-md bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
              >
                {detail.error}
              </p>
            )}

            <TabList aria-label='Run sections'>
              {RUN_TABS.map((tab) => (
                <Tab
                  key={tab.key}
                  active={tab.key === active}
                  onClick={() => select(tab.key)}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>

            {active === 'report' && (
              <RunReportTab report={detail.report} working={working} />
            )}

            {active === 'chat' && (
              <RunChatPanel
                chat={chat}
                working={working}
                onOpenReport={setOpenReport}
                composer={
                  <RunComposer
                    placeholder='Ask the analyst a question'
                    chatState={detail.chat_state}
                    sending={ask.isPending}
                    error={askError}
                    onSend={(text) => ask.mutate(text)}
                  />
                }
              />
            )}
          </div>
        )}
      </PageBody>

      <ReportDialog
        title={openReport?.title ?? 'Report'}
        report={openReport?.report ?? null}
        onClose={() => setOpenReport(null)}
      />
    </div>
  );
}
